import { useEffect, useMemo, useState } from 'react';
import { Project } from 'api/project';
import { FieldOptionBase } from 'api/tasks';
import {
  FieldType,
  MultiSelectField,
  SourceType,
} from 'components/organisms/tasks/fields';
import { useTranslation } from 'hooks/use-translation';
import { useProjectsQuery } from 'queries';
import { getRandomColor } from 'utils/get-random-color';
import { sortByText } from 'utils/sort-by-text';

const computePhases = (
  projects: Project[],
  oldPhaseOptions: FieldOptionBase<number>[],
) => {
  const oldComputedPhaseNames = Object.values(oldPhaseOptions).map(
    (option) => option.label,
  );
  const computedPhases: Record<string, FieldOptionBase<number>> = {};
  let shouldUpdate = false;

  projects?.forEach((project) => {
    project?.projectPhases?.forEach((phase) => {
      if (!phase) {
        return;
      }
      if (!oldComputedPhaseNames.includes(phase.projectPhaseName)) {
        shouldUpdate = true;
      }
      computedPhases[phase.projectPhaseName] = {
        label: phase.projectPhaseName,
        value: phase.projectPhaseName,
        color: getRandomColor(phase.projectPhaseId),
        active: true,
      } as unknown as FieldOptionBase;
    });
  });

  if (shouldUpdate) {
    return Object.values(computedPhases).sort((a, b) =>
      sortByText(a.label, b.label),
    );
  }

  return oldPhaseOptions;
};

export const useCurrentPhasesField = (
  fieldId: number,
): {
  currentPhasesField: MultiSelectField;
  isLoading: boolean;
} => {
  const { data: projects = [], isLoading: isProjectsLoading } =
    useProjectsQuery();
  // the following mechanism is followed to ensure reference to phaseOptions changes only when required. Neither select in useQuery nor useMemo helps here.
  const [phaseOptions, setPhaseOptions] = useState<FieldOptionBase<number>[]>(
    [],
  );

  useEffect(() => {
    setPhaseOptions((oldPhaseOptions) => {
      return computePhases(projects, oldPhaseOptions);
    });
  }, [projects]);

  const fieldLabel = useTranslation('project.fields.current_phases');

  return useMemo(
    () => ({
      currentPhasesField: {
        fieldId,
        fieldName: 'currentPhases',
        fieldLabel,
        type: FieldType.MultiSelect,
        private: false,
        userCreated: false,
        metaData: {
          options: phaseOptions,
        },
        sourceType: SourceType.Project,
      },
      isLoading: isProjectsLoading,
    }),
    [fieldId, fieldLabel, isProjectsLoading, phaseOptions],
  );
};
